import { assertNever } from '@dadohr/shared/domainTypes/00-assertNever';
import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  BatchMode,
  IExperience,
  ITaskTemplate,
  PARTICIPANT_ACTOR_KEY,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { ScheduleChannelType } from '@dadohr/shared/domainTypes/schedules';
import { partition } from 'lodash';
import shortid from 'shortid';
import { ONE_DAY } from '../time';

export type TaskStats = {
  total: number;
  totalInTime: number;
  totalLate: number;
  totalInProgress: number;
  totalDone: number;

  doneInTime: number;
  doneLate: number;
  inProgressOnTime: number;
  inProgressLate: number;

  notStarted: number;
  skipped: number;
};

export const EMPTY_TASK_STATS = (): TaskStats => ({
  total: 0,
  totalInTime: 0,
  totalLate: 0,
  totalInProgress: 0,
  totalDone: 0,
  notStarted: 0,

  doneInTime: 0,
  doneLate: 0,
  inProgressOnTime: 0,
  inProgressLate: 0,
  skipped: 0
});

export const getTaskStats = (
  instances: Doc<IExperience>[]
): { [taskTplId: string]: TaskStats } => {
  const nowMs = Date.now();
  const s: { [taskTplId: string]: TaskStats } = {};
  instances.forEach((instance) => {
    const { tasks } = instance.data;
    tasks.forEach((t) => {
      const c = (s[t.templateId] = s[t.templateId] || EMPTY_TASK_STATS());
      c.total++;
      // An active (NOT_STARTED/RUNNING) task past its deadline is late even
      // before the BE sweep flips it to LATE — count it as late so the People-tab
      // pills match the Schedule (both surface overdue tasks as errors).
      const isOverdueActive =
        !!t.timing?.end &&
        t.timing.end.toMillis() < nowMs &&
        (t.status === TaskStatus.NOT_STARTED ||
          t.status === TaskStatus.RUNNING);
      if (isOverdueActive) {
        c.totalLate++;
        c.totalInProgress++;
        c.inProgressLate++;
        return;
      }
      if (t.status === TaskStatus.NOT_STARTED) {
        c.notStarted++;
        return;
      }
      if (t.status === TaskStatus.RUNNING) {
        c.totalInTime++;
        c.totalInProgress++;
        c.inProgressOnTime++;
        return;
      }
      if (t.status === TaskStatus.LATE) {
        c.totalLate++;
        c.totalInProgress++;
        c.inProgressLate++;
        return;
      }
      if (t.status === TaskStatus.COMPLETE) {
        if (t.completedLate) {
          c.doneLate++;
          c.totalLate++;
        } else {
          c.doneInTime++;
          c.totalInTime++;
        }
        c.totalDone++;
        return;
      }
      if (t.status === TaskStatus.SKIPPED) {
        c.skipped++;
        return;
      }

      if (t.status === TaskStatus.PAST_DEADLINE) {
        //TODO
        return;
      }

      assertNever(t.status);
    });
  });
  return s;
};

export const getEmptyTaskTemplate = (
  preferUserTimezone: boolean,
  startAndEnd?: { start?: number; end?: number }
) => {
  const start = startAndEnd?.start ? startAndEnd.start : 0;
  const end = startAndEnd?.end ? startAndEnd.end : start + ONE_DAY * 5;
  const tpl: ITaskTemplate = {
    id: shortid(),
    batchMode: BatchMode.DEFAULT,
    catchup: { disabled: false },
    actor: PARTICIPANT_ACTOR_KEY,
    title: 'Unnamed Task',
    description: {
      t: '',
      v: 1
    },
    start: 0,
    end: ONE_DAY * 5,
    startTime: null,
    timing: {
      type: 'fixed',
      start,
      end,
      time: null
    },

    preferUserTimezone,

    reminders: [
      {
        id: shortid(),
        afterStart: null,
        beforeEnd: ONE_DAY * 1,
        v: 1
      }
    ],
    remindUntilDone: {
      frequency: 'INFINITE',
      interval: ONE_DAY * 1,
      offset: 0
    },
    remindersV2: [
      {
        id: shortid(),
        relativeDistanceToDeadline: -ONE_DAY,
        v: 2
      },
      {
        id: shortid(),
        relativeDistanceToDeadline: 0,
        v: 2
      }
    ],
    blocks: [],
    audiences: [
      {
        id: shortid(),
        rule: 'EVERYONE',
        name: '',
        color: '',
        completionCriteria: [],
        blocks: []
      }
    ],

    subTasks: [],
    customCompletionCriteria: [],
    channels: [{ type: ScheduleChannelType.EMAIL, emailFrom: 'email' }],
    v: 1
  };

  return tpl;
};

export const getInstancesThatHaveThisTask = (
  instances: Doc<IExperience>[],
  taskTplId: string
) => {
  const [instancesThatHaveThisTask] = partition(instances, (instance) => {
    const task: IExperience['tasks'] = instance.data.tasks.filter(
      (t) => t.templateId === taskTplId
    );
    return !!task; // if this is empty -> then they don't receive the task. They are not part of an audience that gets this
  });

  return instancesThatHaveThisTask; // To be updated if we need instances that are not part of the audience
};
